import type { ReactNode } from 'react';
import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { type AccountNet, accountNetFromView } from '@/lib/accountNet';
import { mapCasesToDebts } from '@/lib/api';
import { getCaseData } from '@/lib/core-api';
import { getPayerEmailFromConnect } from '@/lib/connect-api';

import { getAccountData } from '@/lib/database-api';
import { type ActiveDiscountOffer, getActiveDiscountOffer } from '@/lib/discount-offers';
import { normalizeMaskedEmail } from '@/lib/masked-email';
import { getMockDebts } from '@/lib/mockData';
import type { DebtData } from '@/types';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';

/** Account data from Core API */
export interface AccountData {
  id: string;
  external_account_ref: string | null;
  account_type: string | null;
  created_on: string | null;
  has_access_code: boolean;
  preferred_language: string | null;
  /** Masked preferred email, or null when the customer has none on file. */
  customer_email: string | null;
}

interface DebtDataContextValue {
  debts: DebtData[];
  account: AccountData | null;
  /**
   * The active account-scoped payment plan for this account, if any. Mutually
   * exclusive with per-case plans on debts[]. When set, the `/account-payment`
   * page renders an "active plan" banner and hides the create-plan tab.
   */
  accountPaymentPlan: OphelosPaymentPlanResponse | null;
  /**
   * The account's credit and what it can be charged. Null when the account's open cases
   * span currencies (no single total) and in mock mode.
   */
  accountNet: AccountNet | null;
  isLoading: boolean;
  error: string | null;
  refreshDebts: () => Promise<void>;
  /** Active discount offers keyed by case_id. Empty in mock mode. Loaded asynchronously after debts so the page isn't blocked. */
  activeOffers: Record<string, ActiveDiscountOffer>;
  offersLoading: boolean;
}

const DebtDataContext = createContext<DebtDataContextValue | undefined>(undefined);

interface DebtDataProviderProps {
  children: ReactNode;
}

/**
 * DebtDataProvider - Manages global debt data state
 *
 * Handles:
 * - Loading debt data on mount and accountId change
 * - Mock data for preview/demo mode
 * - Core-api first, then database fallback
 * - Preview mode context updates via postMessage
 * - Loading states to prevent showing €0.00 before data loads
 */
export function DebtDataProvider({ children }: DebtDataProviderProps) {
  const [debts, setDebts] = useState<DebtData[]>([]);
  const [account, setAccount] = useState<AccountData | null>(null);
  const [accountPaymentPlan, setAccountPaymentPlan] = useState<OphelosPaymentPlanResponse | null>(null);
  const [accountNet, setAccountNet] = useState<AccountNet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeOffers, setActiveOffers] = useState<Record<string, ActiveDiscountOffer>>({});
  const [offersLoading, setOffersLoading] = useState(false);
  const { accountId, authStage, getAccessToken, isLoading: authIsLoading } = useAuth();
  const { useMockData } = useConfiguration();

  // Refs to prevent unnecessary reloads in preview mode
  const hasLoadedRef = useRef(false);
  const lastContextRef = useRef<string | null>(null);
  const lastAccountIdRef = useRef<string | null>(null);

  const refreshOffers = useCallback(
    async (forDebts: DebtData[]) => {
      // RPC failures are swallowed inside getActiveDiscountOffer so allSettled
      // never rejects. Discount UI is additive and must never break the page.
      setOffersLoading(true);
      const results = await Promise.allSettled(
        forDebts.map((d) => getActiveDiscountOffer(d.id, authStage, getAccessToken))
      );
      const offersByCase: Record<string, ActiveDiscountOffer> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          offersByCase[forDebts[i].id] = r.value;
        }
      });
      setActiveOffers(offersByCase);
      setOffersLoading(false);
    },
    [authStage, getAccessToken]
  );

  const loadDebts = useCallback(async () => {
    // Wait for auth to initialize before attempting to load data
    if (authIsLoading) {
      return;
    }

    if (!accountId) {
      setIsLoading(false);
      setDebts([]);
      setAccount(null);
      setAccountPaymentPlan(null);
      setAccountNet(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Preview/demo mode: use mock data
      if (useMockData) {
        const contextStr = sessionStorage.getItem('hub_preview_context');
        if (contextStr === lastContextRef.current && hasLoadedRef.current && lastAccountIdRef.current === accountId) {
          setIsLoading(false);
          return;
        }
        lastContextRef.current = contextStr;
        lastAccountIdRef.current = accountId;
        setDebts(getMockDebts());
        // Mock account data for preview mode
        setAccount({
          id: accountId,
          external_account_ref: 'MOCK-1234',
          account_type: 'individual',
          created_on: new Date().toISOString(),
          has_access_code: true,
          preferred_language: 'en',
          customer_email: 'm***@example.com',
        });
        setAccountPaymentPlan(null); // No mock account-scoped plan today
        hasLoadedRef.current = true;
        setIsLoading(false);
        return;
      }

      // Fetch account + customer data from database (for metadata)
      const accountData = await getAccountData(accountId, getAccessToken);

      if (!accountData) {
        throw new Error('Account data not found');
      }
      // Fetch full account view from Core API (cases + account-scoped plan) alongside
      // the masked *contactable* payer email. The account view runs under Connect ownership and
      // can't see contact_detail (its customer_email is always null here); the Connect
      // contacts endpoint reads it under an elevated role using the same contactable
      // predicate the checkout resolution uses, so presence and resolution agree.
      const [accountView, payerContact] = await Promise.all([
        getCaseData(accountId, authStage, getAccessToken),
        getPayerEmailFromConnect(accountId, authStage, getAccessToken).catch(() => ({ email: null })),
      ]);

      // Combine database account metadata with Core API case financial data.
      // Account-scoped plan (if any) is threaded into per-case mapping so
      // member cases surface it as their currently-covering plan.
      const mappedDebts = mapCasesToDebts(
        accountView.case_details,
        accountData,
        accountView.account_payment_plan
      );

      setDebts(mappedDebts);
      setAccountPaymentPlan(accountView.account_payment_plan);
      setAccountNet(accountNetFromView(accountView));
      setAccount({
        id: accountData.account_id,
        external_account_ref: accountData.account_external_ref,
        account_type: accountData.account_type,
        created_on: accountData.account_created_on,
        has_access_code: true, // Inferred: user logged in with access code or JWT
        preferred_language: accountData.account_preferred_language,
        // Email presence for the capture field, from the contactable-predicate endpoint
        // (the only source visible under Connect ownership). It masks a blank/removed email to
        // '***' (no '@'); treat null, blank, or any non-email value as "no usable email"
        // so the field renders in those cases.
        customer_email: normalizeMaskedEmail(payerContact.email),
      });
      hasLoadedRef.current = true;
      lastAccountIdRef.current = accountId;

      // Fire-and-forget: fetch active discount offers per case. Non-blocking —
      // discount UI is additive and must never delay the page.
      void refreshOffers(mappedDebts);
    } catch (err) {
      console.error('[DebtDataContext] Error loading debt data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load debt data');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, useMockData, authStage, getAccessToken, authIsLoading]);

  // Load debts when accountId changes or on mount
  useEffect(() => {
    // Reset state when accountId changes
    if (!accountId) {
      setDebts([]);
      setAccount(null);
      setAccountPaymentPlan(null);
      setAccountNet(null);
      setIsLoading(false);
      hasLoadedRef.current = false;
      lastContextRef.current = null;
      lastAccountIdRef.current = null;
      return;
    }

    // Only load if we haven't loaded yet or accountId changed
    if (!hasLoadedRef.current || lastAccountIdRef.current !== accountId) {
      loadDebts();
    }
  }, [accountId, loadDebts]);

  // Listen for context updates in preview/demo mode
  useEffect(() => {
    if (!useMockData) return;

    const handleMessage = (event: MessageEvent) => {
      const isLocalhost =
        event.origin.startsWith('http://localhost:') ||
        event.origin.startsWith('https://localhost') ||
        event.origin.startsWith('http://127.0.0.1:') ||
        event.origin.startsWith('https://127.0.0.1:') ||
        event.origin.startsWith('https://hub.localhost');

      if (!isLocalhost || event.data?.type !== 'CONTEXT_UPDATE') return;

      const contextStr = JSON.stringify(event.data.payload);
      if (contextStr !== lastContextRef.current) {
        lastContextRef.current = contextStr;
        setDebts(getMockDebts());
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [useMockData]);

  // Refresh function for manual reloads
  const refreshDebts = useCallback(async () => {
    hasLoadedRef.current = false;
    await loadDebts();
  }, [loadDebts]);

  // Refetch offers when the earliest expiry passes, so the UI reflects expired
  // offers without the user reloading. One-shot timer, rescheduled after each
  // refresh. No timer when there are no offers or none have expiry timestamps.
  useEffect(() => {
    const expiries = Object.values(activeOffers)
      .map((o) => (o.expiresAt ? new Date(o.expiresAt).getTime() : null))
      .filter((t): t is number => t != null);
    if (expiries.length === 0 || debts.length === 0) return;
    const soonest = Math.min(...expiries);
    const delayMs = soonest - Date.now() + 1_000; // +1s grace so the server has also expired
    if (delayMs <= 0) {
      void refreshOffers(debts);
      return;
    }
    // setTimeout stores delay as a 32-bit signed int (~24.8 days). Any larger
    // value fires immediately, which would loop refreshOffers indefinitely for
    // a campaign with a far-future ends_at. Clamp below the limit so we wake
    // up once, refetch, and reschedule from a now-closer expiry.
    const MAX_TIMEOUT_MS = 2_000_000_000;
    const handle = window.setTimeout(
      () => {
        void refreshOffers(debts);
      },
      Math.min(delayMs, MAX_TIMEOUT_MS)
    );
    return () => window.clearTimeout(handle);
  }, [activeOffers, debts, refreshOffers]);

  return (
    <DebtDataContext.Provider
      value={{
        debts,
        account,
        accountPaymentPlan,
        accountNet,
        isLoading,
        error,
        refreshDebts,
        activeOffers,
        offersLoading,
      }}
    >
      {children}
    </DebtDataContext.Provider>
  );
}

/**
 * Hook to access debt data from context
 *
 * @example
 * ```tsx
 * const { debts, isLoading, refreshDebts } = useDebtData();
 *
 * if (isLoading) return <Loader />;
 *
 * const debt = debts.find(d => d.id === debtId);
 * ```
 */
export function useDebtData(): DebtDataContextValue {
  const context = useContext(DebtDataContext);
  if (context === undefined) {
    throw new Error('useDebtData must be used within a DebtDataProvider');
  }
  return context;
}
